import { Box, createStyles, makeStyles, Grid, Typography, Card, CardContent, Button, FilledInput, InputAdornment } from "@material-ui/core";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useRouter } from "next/router";
import { Email } from "@material-ui/icons";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import IndexContainerSignup from "../../src/components/index-container-signup";
import { useEffect, useRef, useState } from "react";
import { LoadingButton } from "@onpreo/components";
import { regexps } from "@onpreo/components/src/regex";

const recoverPassword = (data: any) => {
    const url = `/api/user/password-reset`;
    return axios.post(url, { ...data });
};

const userService = {
    recoverPassword
};

const passwordForgotSchema = yup.object().shape({
    email: yup.string().matches(regexps.emailRFC, "Bitte geben Sie eine gültige E-Mail Adresse ein").required("Eintrag kann nicht leer gelassen werden")
});

const useStyles = makeStyles(theme =>
    createStyles({
        container: {
            height: "100vh",
            backgroundColor: "#002667"
        },
        containerBackground: {
            height: "428.41px",
            background: "linear-gradient(90deg, #D5D5D5 0%, #012767 99.99%, #002667 100%)"
        },
        headerGrid: {
            paddingTop: "50px",
            lineHeight: "145%",
            display: "flex",
            color: "#FFFFFF"
        },
        headerText: {
            color: "#fff",
            fontWeight: 500
        },
        subheaderGrid: {
            paddingTop: "30px",
            display: "flex",
            color: "#FFFFFF"
        },
        subheaderText: {
            color: "#fff",
            fontWeight: 400
        },
        spacer: {
            padding: theme.spacing(0, 2),
            marginTop: theme.spacing(10)
        },
        filledInput: {
            width: "100%",
            height: "46px",
            marginTop: theme.spacing(4)
        },
        passwordForgotCard: {
            marginTop: "80px",
            minWidth: 420,
            minHeight: 420,
            padding: 16,
            backgroundColor: "#F7FAFC",
            boxShadow: "0px 0px 32px rgba(136, 152, 170, 0.15)",
            borderRadius: "6px"
        },
        cardHeaderText: { fontWeight: "normal", fontSize: "17px", color: "#8898AA" },
        submitButton: {}
    })
);

export default function PasswordReset() {
    const classes = useStyles();

    const router = useRouter();

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch
    } = useForm({ defaultValues: { email: "" }, resolver: yupResolver(passwordForgotSchema) });

    const [emailNotFound, setEmailNotFound] = useState("");

    const watchState = watch();

    const enteredEmail = useRef("");

    const handleChangePassword: SubmitHandler<{ email: string }> = async data => {
        try {
            const result = await userService.recoverPassword(data);
            if (result.status === 201) {
                await router.push("/forgot-password/email-sent-success");
            }
        } catch (err) {
            enteredEmail.current = watchState.email;
            setEmailNotFound("Email addresse ist nicht bekannt!");
        }
    };

    useEffect(() => {
        if (enteredEmail?.current !== watchState?.email) setEmailNotFound("");
    }, [emailNotFound, watchState]);

    return (
        <IndexContainerSignup>
            <Grid justifyContent={"center"} className={classes.headerGrid}>
                <Typography variant={"h1"} className={classes.headerText}>
                    Sie haben Ihr Passwort vergessen? Kein Problem!
                </Typography>
            </Grid>
            <Grid justifyContent={"center"} className={classes.subheaderGrid}>
                <Typography variant={"h3"} className={classes.subheaderText}>
                    Nutzen Sie das unten angezeigte Formular, um Ihr Passwort zu ändern.
                </Typography>
            </Grid>
            <Grid container xs={12} justifyContent={"center"}>
                <Card className={classes.passwordForgotCard}>
                    <CardContent>
                        <Grid container justifyContent={"center"}>
                            <Typography className={classes.cardHeaderText}>Geben Sie Ihre E-Mail-Adresse ein, um Ihr Passwort zurückzusetzen</Typography>
                        </Grid>
                        <form>
                            <div className={classes.spacer}>
                                <Grid container justifyContent={"center"} alignItems={"center"} direction={"column"}>
                                    <Grid item style={{ width: "100%" }}>
                                        <Controller
                                            control={control}
                                            name="email"
                                            render={({ field }) => (
                                                <FilledInput
                                                    {...field}
                                                    className={classes.filledInput}
                                                    placeholder="E-Mail"
                                                    autoComplete="off"
                                                    required
                                                    type="email"
                                                    error={errors.email || emailNotFound !== "" ? true : false}
                                                    startAdornment={
                                                        <InputAdornment position="start">
                                                            <Email style={{ width: 16, height: 16 }} />
                                                        </InputAdornment>
                                                    }
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item style={{ width: "100%" }}>
                                        <Typography variant="inherit" color="error">
                                            {errors?.email?.message}
                                            {emailNotFound}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </div>
                            <div className={classes.spacer}>
                                <Grid container justifyContent={"center"}>
                                    <LoadingButton
                                        type="submit"
                                        variant="contained"
                                        color={"primary"}
                                        isRedirect={false}
                                        onClick={handleSubmit(handleChangePassword)}
                                    >
                                        Passwort zurücksetzen
                                    </LoadingButton>
                                </Grid>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </Grid>
        </IndexContainerSignup>
    );
}
